import type { Spec } from "@json-render/core";
import { isNonEmptySpec, validateSpec } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";
import { IconNameSchema } from "./icons";
import { isSafeHref, isSafeHrefTemplate } from "./link";

/**
 * The Apps component catalog: the vocabulary an App's view is written in.
 *
 * Server-safe on purpose — only component names, Zod prop schemas and
 * descriptions — so the backend can validate an App's view and agents can
 * generate one without pulling `@erp/ui` (browser-only) into Node. The React
 * implementations live in `./react/components.tsx`.
 *
 * The vocabulary follows DESIGN.md ("The Warm Operator"): dense 8px rhythm,
 * semantic color only, few cards, sentence case. Descriptions are
 * written for the agent that authors the view, so they say *when* to use a
 * component, not just what it is.
 */

const spacing = z
  .enum(["none", "xs", "sm", "md", "lg", "xl"])
  .describe(
    "Spacing step (xs=4px, sm=8px, md=12px, lg=16px, xl=24px). sm is the default rhythm; lg+ is rare in operational UI.",
  );
const alignment = z.enum(["start", "center", "end", "stretch"]);
const justification = z.enum(["start", "center", "end", "between"]);
const tone = z
  .enum(["default", "muted", "success", "warning", "destructive"])
  .describe(
    "Semantic tone. Color only ever signals status — never decoration.",
  );

/** How a value is formatted for display. Shared by Field, Metric and Table columns. */
export const AppValueFormatSchema = z
  .enum([
    "text",
    "number",
    "currency",
    "percent",
    "date",
    "datetime",
    "mono",
    "badge",
  ])
  .describe(
    "text (default); number/currency/percent use tabular digits; percent expects a ratio (0.12 → 12%); date/datetime accept ISO strings; mono for ids and codes; badge renders the value as a status pill.",
  );
export type AppValueFormat = z.infer<typeof AppValueFormatSchema>;

const currency = z
  .string()
  .regex(/^[A-Z]{3}$/)
  .describe("ISO 4217 code for currency formatting. Defaults to USD.");

const placementSide = z.enum(["top", "right", "bottom", "left"]);
const placementAlign = z.enum(["start", "center", "end"]);

const option = z.object({ value: z.string(), label: z.string() });

const icon = IconNameSchema.optional();

const href = z
  .string()
  .refine(isSafeHref, {
    message:
      "href must be an app path starting with /, or an http(s):// or mailto: URL.",
  })
  .describe(
    "Where the link goes: an in-app path (/suppliers/42) or an http(s):// / mailto: URL. Anything else (javascript:, data:) is rejected.",
  );

const chartRows = z
  .array(z.record(z.string(), z.unknown()))
  .describe(
    'Row objects, one per mark. Usually bound: { "$state": "/rows" }. Aggregate before charting: one row per bar, point or slice.',
  );

const chartHeight = z
  .number()
  .int()
  .min(120)
  .max(800)
  .optional()
  .describe("Total chart height in pixels. Default 280.");

const chartValueFormat = AppValueFormatSchema.extract([
  "number",
  "currency",
  "percent",
])
  .optional()
  .describe(
    "How values read on the value axis and in tooltips: number, currency or percent (a ratio, 0.12 → 12%). Omit for the chart's default.",
  );

const fieldType = z
  .enum(["quantitative", "temporal", "nominal", "ordinal"])
  .describe(
    "Scale of the field: quantitative=number, temporal=date, nominal=unordered category, ordinal=ordered category.",
  );

const stackMode = z
  .union([z.boolean(), z.literal("normalize")])
  .describe(
    "With `color`: true stacks to totals, normalize stacks to 100%, false groups side by side.",
  );

/** The first `url` or `href` key in a chart spec, which would let it reach outside the chart (mirrors @erp/viz's check). */
function findExternalReference(value: unknown, path = ""): string | null {
  if (typeof value !== "object" || value === null) return null;
  for (const [key, child] of Object.entries(value)) {
    const at = `${path}/${key}`;
    if (key === "url" || key === "href") return at;
    const hit = findExternalReference(child, at);
    if (hit) return hit;
  }
  return null;
}

export const APP_COMPONENTS = {
  // ── Layout ────────────────────────────────────────────────────────────────
  Stack: {
    props: z.object({
      gap: spacing.optional(),
      padding: spacing.optional(),
      align: alignment.optional(),
    }),
    slots: ["default"],
    description:
      "Vertical stack. The usual root of a view. Siblings sit one spacing step apart (sm by default).",
  },
  Inline: {
    props: z.object({
      gap: spacing.optional(),
      align: alignment.optional(),
      justify: justification.optional(),
      wrap: z.boolean().optional(),
    }),
    slots: ["default"],
    description:
      "Horizontal row whose children keep their NATURAL width — badges, buttons, a label beside a value. For cards side by side use Grid. A justify: between row needs content at BOTH ends: never a row of trailing controls with nothing at its start — put them beside the view's heading instead.",
  },
  Grid: {
    props: z.object({
      columns: z
        .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
        .optional()
        .describe(
          "Equal-width columns (default 2). Collapses to one column on narrow screens.",
        ),
      gap: spacing.optional(),
    }),
    slots: ["default"],
    description:
      "Equal-width responsive grid. Use it to place cards, metrics or fields side by side.",
  },
  Card: {
    props: z.object({
      title: z.string().optional(),
      description: z.string().optional(),
    }),
    slots: ["default"],
    description:
      "A bordered box, and the exception: most views need none. A Card is only for a discrete object that REPEATS and is handled as a unit — several locations, options or items the user scans and compares, side by side in a Grid. Never for the view's main record, a group of figures, a form, a table, a list, a tab panel, an empty state or an error; those go in a Section. Never nest a Card in a Card, and never put a Separator directly above or below one.",
  },
  Section: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
    }),
    slots: ["default"],
    description:
      "A titled region that sits directly on the page (no border). The default way to group figures, fields, a table, a list or a form under a heading.",
  },
  Tabs: {
    props: z.object({
      tabs: z
        .array(option)
        .min(1)
        .describe(
          "The tab strip, in order. Each `value` matches one child Tab.",
        ),
      value: z
        .string()
        .optional()
        .describe(
          'Selected tab. Defaults to the first; bind { "$bindState": "/tab" } to read or drive it.',
        ),
    }),
    slots: ["default"],
    description:
      "Tabbed views. Children MUST be Tab elements; only the selected Tab renders.",
  },
  Tab: {
    props: z.object({ value: z.string() }),
    slots: ["default"],
    description: "One tab panel inside Tabs, matched to the strip by `value`.",
  },
  Separator: {
    props: z.object({}),
    slots: [],
    description:
      "A 1px divider between regions. Use it — not a Card — to separate parts of a view, and to mark where secondary, below-the-fold content begins. Not next to a Card: the Card's border is already the boundary.",
  },

  // ── Content ───────────────────────────────────────────────────────────────
  Heading: {
    props: z.object({
      text: z.string(),
      level: z
        .union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
        .optional()
        .describe("1=display, 2=headline, 3=title (default), 4=subtitle."),
    }),
    slots: [],
    description: "A heading. Sentence case, never Title Case.",
  },
  Text: {
    props: z.object({
      text: z.string(),
      variant: z
        .enum(["body", "label", "muted", "eyebrow"])
        .optional()
        .describe("eyebrow is the only uppercase style in the system."),
      tone: tone.optional(),
    }),
    slots: [],
    description: "A run of text. 14px body by default.",
  },
  Field: {
    props: z.object({
      label: z.string(),
      value: z
        .unknown()
        .describe('Often bound: { "$state": "/record/total" }.'),
      format: AppValueFormatSchema.optional(),
      currency: currency.optional(),
    }),
    slots: [],
    description:
      "A read-only label over a value, for record details. Empty values render an em dash. Put several in a Grid.",
  },
  Metric: {
    props: z.object({
      label: z.string(),
      value: z.unknown(),
      format: AppValueFormatSchema.optional(),
      currency: currency.optional(),
      delta: z
        .number()
        .optional()
        .describe("Change vs. the comparison period, as a ratio (0.04 → +4%)."),
      deltaLabel: z.string().optional().describe('e.g. "vs last month".'),
      positiveIsGood: z
        .boolean()
        .optional()
        .describe(
          "Whether an increase is good (green). Default true; false for costs, days late, etc.",
        ),
    }),
    slots: [],
    description:
      "A compact figure with a label and an optional delta. Put a few in a Grid under a Section, never in a Card; never use a lone oversized number as decoration.",
  },
  Badge: {
    props: z.object({
      text: z.string(),
      tone: z
        .enum([
          "default",
          "secondary",
          "outline",
          "success",
          "warning",
          "destructive",
        ])
        .optional(),
      icon,
    }),
    slots: [],
    description: "A status pill. Tone signals state. An icon is optional.",
  },
  Alert: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
      tone: z.enum(["default", "destructive"]).optional(),
    }),
    slots: [],
    description:
      "A boxed callout, for a failure or a blocker the user cannot act past (a load error, a missing permission). Rarely the right choice: do NOT use it to restate what a badge, a row or a figure already shows — let the status badge carry it.",
  },
  AgentNote: {
    props: z.object({
      title: z.string().optional(),
      text: z.string(),
    }),
    slots: ["default"],
    description:
      "Text the App's agent wrote — a summary, a recommendation, a proposed change awaiting a human. Uses the olive agent color so users can tell agent output from system data.",
  },
  EmptyState: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
      actionLabel: z
        .string()
        .optional()
        .describe("Label for the single action button; emits `press`."),
    }),
    slots: [],
    description:
      "Centered message for a view with nothing to show. At most one action.",
  },
  Progress: {
    props: z.object({
      value: z.number().describe("0–100."),
      label: z.string().optional(),
    }),
    slots: [],
    description: "A thin determinate progress bar with an optional label.",
  },
  Skeleton: {
    props: z.object({
      lines: z.number().int().min(1).max(12).optional(),
    }),
    slots: [],
    description: "Loading placeholder lines, shown while the App's data loads.",
  },

  // ── Data ──────────────────────────────────────────────────────────────────
  Table: {
    props: z.object({
      columns: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          format: AppValueFormatSchema.optional(),
          currency: currency.optional(),
          align: z
            .enum(["left", "right", "center"])
            .optional()
            .describe(
              "Defaults to right for number/currency/percent, left otherwise.",
            ),
          href: z
            .string()
            .refine(isSafeHrefTemplate, {
              message:
                "href must be an app path starting with /, or an http(s):// or mailto: URL.",
            })
            .optional()
            .describe(
              'Renders the cell as a link: a template filled from the row, e.g. "/invoices/{id}". A row missing a referenced field shows plain text.',
            ),
          sortable: z
            .boolean()
            .optional()
            .describe(
              "Whether the header offers sorting. Default true; leave it on.",
            ),
          filterable: z
            .boolean()
            .optional()
            .describe(
              "Whether the header offers filtering by value. Default true for text, mono, date and badge columns; false for numeric ones.",
            ),
        }),
      ),
      rows: z
        .array(z.record(z.string(), z.unknown()))
        .describe(
          'Row objects keyed by column `key`. Usually bound: { "$state": "/rows" }.',
        ),
      emptyText: z.string().optional(),
      rowActionLabel: z
        .string()
        .optional()
        .describe("Shorthand for a single row action (value `default`)."),
      rowActions: z
        .array(
          z.object({
            value: z.string(),
            label: z.string(),
            destructive: z.boolean().optional(),
          }),
        )
        .optional()
        .describe(
          "Actions in each row's trailing ⋯ menu. Choosing one writes the row to /selectedRow and the action's `value` to /selectedRowAction, then emits `rowPress`.",
        ),
    }),
    slots: [],
    description:
      "A dense data table with formatted columns; every header sorts and filters by default. Status columns should use format: badge. Sits directly in the view or a Section — never in a Card.",
  },
  List: {
    props: z.object({
      items: z.array(
        z.object({
          title: z.string(),
          description: z.string().optional(),
          meta: z
            .string()
            .optional()
            .describe("Trailing muted text, e.g. a date or amount."),
          badge: z.string().optional(),
          badgeTone: z
            .enum([
              "default",
              "secondary",
              "outline",
              "success",
              "warning",
              "destructive",
            ])
            .optional(),
        }),
      ),
      emptyText: z.string().optional(),
    }),
    slots: [],
    description:
      "A compact list of items — activity, exceptions, recent records.",
  },

  // ── Charts ────────────────────────────────────────────────────────────────
  BarChart: {
    props: z.object({
      data: chartRows,
      x: z
        .string()
        .describe(
          "Field for the category axis. Always the category, also when horizontal.",
        ),
      y: z
        .string()
        .describe(
          "Field for the value axis. Always the value, also when horizontal.",
        ),
      color: z
        .string()
        .optional()
        .describe("Field that splits bars into a colored series."),
      stack: stackMode.optional(),
      horizontal: z
        .boolean()
        .optional()
        .describe(
          "Draw bars sideways (categories down the side); x and y keep their meaning. Use for long category names or a ranked top-N.",
        ),
      xType: fieldType.optional(),
      valueFormat: chartValueFormat,
      currency: currency.optional(),
      valueLabels: z
        .boolean()
        .optional()
        .describe("Write each bar's value at its end."),
      title: z.string().optional(),
      height: chartHeight,
    }),
    slots: [],
    description:
      "Compare values across categories, or a series across categories with `color`. The first choice for 'X by Y'. Sits directly in a Section, never in a Card.",
  },
  LineChart: {
    props: z.object({
      data: chartRows,
      x: z.string().describe("Field for the x axis (a date by default)."),
      y: z.string().describe("Field for the value axis."),
      color: z
        .string()
        .optional()
        .describe("Field that splits the data into one line per value."),
      xType: fieldType
        .optional()
        .describe(
          "Defaults to temporal. Set ordinal or nominal when x is not a date.",
        ),
      point: z.boolean().optional().describe("Mark each data point."),
      valueFormat: chartValueFormat,
      currency: currency.optional(),
      title: z.string().optional(),
      height: chartHeight,
    }),
    slots: [],
    description:
      "A trend over time, or a few series compared over time. Prefer it to a bar chart when order and slope matter.",
  },
  AreaChart: {
    props: z.object({
      data: chartRows,
      x: z.string().describe("Field for the x axis (a date by default)."),
      y: z.string().describe("Field for the value axis."),
      color: z
        .string()
        .optional()
        .describe("Field that splits the area into stacked series."),
      stack: stackMode.optional(),
      xType: fieldType
        .optional()
        .describe(
          "Defaults to temporal. Set ordinal or nominal when x is not a date.",
        ),
      title: z.string().optional(),
      height: chartHeight,
    }),
    slots: [],
    description:
      "A volume over time, or how a total divides into series over time (stack: true, or normalize for share). Use LineChart when only the trend matters.",
  },
  PieChart: {
    props: z.object({
      data: chartRows,
      category: z.string().describe("Field naming each slice."),
      value: z.string().describe("Field sizing each slice."),
      donut: z.boolean().optional(),
      valueFormat: chartValueFormat,
      currency: currency.optional(),
      title: z.string().optional(),
      height: chartHeight,
    }),
    slots: [],
    description:
      "Share of a whole across FEW slices (five or fewer). More than that, or when slices must be compared precisely, use a horizontal BarChart.",
  },
  ScatterChart: {
    props: z.object({
      data: chartRows,
      x: z.string(),
      y: z.string(),
      color: z.string().optional().describe("Field that colors each point."),
      size: z.string().optional().describe("Field that sizes each point."),
      xFormat: chartValueFormat,
      yFormat: chartValueFormat,
      currency: currency.optional(),
      title: z.string().optional(),
      height: chartHeight,
    }),
    slots: [],
    description:
      "The relationship between two numeric fields across many records (cost vs. margin, one dot per SKU).",
  },
  Chart: {
    props: z.object({
      grammar: z
        .enum(["vega-lite", "vega"])
        .describe("The grammar `spec` is written in. Prefer vega-lite."),
      spec: z
        .record(z.string(), z.unknown())
        .superRefine((spec, ctx) => {
          const at = findExternalReference(spec);
          if (at) {
            ctx.addIssue({
              code: "custom",
              message: `Chart specs may not load external data or links (${at}).`,
            });
          }
        })
        .describe(
          "The Vega-Lite or Vega spec, without `data`, `width`, `height`, `config`, `$schema` or `background`; the host owns those and themes the chart. No `url` or `href` keys.",
        ),
      data: chartRows.describe(
        "Rows bound to the chart. Vega-Lite: they become the top-level data. Vega: they fill the data source named `table`. Usually { $state }.",
      ),
      title: z
        .string()
        .optional()
        .describe("Heading shown above the chart (outside the spec)."),
      height: chartHeight,
    }),
    slots: [],
    description:
      "The ESCAPE HATCH for a chart BarChart, LineChart, AreaChart, PieChart and ScatterChart cannot express: layers, facets, heatmaps, histograms, dual series, annotations, full Vega. Reach for the simple charts first. The spec is validated and sandboxed; an invalid one renders an error, not a chart.",
  },

  // ── Visuals ───────────────────────────────────────────────────────────────
  Icon: {
    props: z.object({
      name: IconNameSchema,
      size: z
        .enum(["xs", "sm", "md", "lg"])
        .optional()
        .describe("12, 16 (default), 20 or 24px."),
      tone: tone.optional(),
    }),
    slots: [],
    description:
      "A Phosphor icon, for a status glyph or a visual cue beside text. Never decoration. An unknown name renders nothing. Buttons and Badges take an `icon` prop of their own.",
  },
  Link: {
    props: z.object({
      text: z.string(),
      href,
      icon,
    }),
    slots: [],
    description:
      "A text link. In-app paths (/invoices/42) navigate inside the app; http(s) and mailto links open a new tab with an external-link mark. Use for navigating to a record or a source document; use a Button for actions.",
  },

  // ── Input ─────────────────────────────────────────────────────────────────
  Button: {
    props: z.object({
      label: z.string(),
      variant: z
        .enum([
          "default",
          "outline",
          "secondary",
          "ghost",
          "destructive",
          "link",
        ])
        .optional(),
      size: z.enum(["default", "sm"]).optional(),
      disabled: z.boolean().optional(),
      icon: icon.describe("Icon shown before the label."),
    }),
    slots: [],
    description:
      "Emits `press`. `default` (filled) is for the ONE primary action in view; everything else is outline or ghost.",
  },
  TextInput: {
    props: z.object({
      label: z.string(),
      value: z
        .string()
        .optional()
        .describe('Bind two-way: { "$bindState": "/form/name" }.'),
      placeholder: z.string().optional(),
      description: z.string().optional(),
      type: z.enum(["text", "email", "number", "date"]).optional(),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description: "A labelled single-line input.",
  },
  TextArea: {
    props: z.object({
      label: z.string(),
      value: z
        .string()
        .optional()
        .describe('Bind two-way: { "$bindState": "/form/notes" }.'),
      placeholder: z.string().optional(),
      rows: z.number().int().min(2).max(20).optional(),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description: "A labelled multi-line input.",
  },
  Select: {
    props: z.object({
      label: z.string(),
      value: z
        .string()
        .optional()
        .describe('Bind two-way: { "$bindState": "/form/status" }.'),
      options: z.array(option),
      placeholder: z.string().optional(),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description: "A labelled single-choice dropdown.",
  },
  Checkbox: {
    props: z.object({
      label: z.string(),
      checked: z
        .boolean()
        .optional()
        .describe('Bind two-way: { "$bindState": "/form/agree" }.'),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description: "A labelled checkbox, for choices that apply on submit.",
  },
  Switch: {
    props: z.object({
      label: z.string(),
      description: z.string().optional(),
      checked: z
        .boolean()
        .optional()
        .describe('Bind two-way: { "$bindState": "/settings/enabled" }.'),
      disabled: z.boolean().optional(),
    }),
    slots: [],
    description: "A labelled on/off setting that takes effect immediately.",
  },

  // ── Overlays ──────────────────────────────────────────────────────────────
  Popover: {
    props: z.object({
      title: z.string().optional(),
      side: placementSide.optional(),
      align: placementAlign.optional(),
    }),
    slots: ["default"],
    description:
      "A small floating panel opened by clicking its trigger, for a few secondary fields, a short form or extra detail that would clutter the view. The FIRST child is the trigger (usually a Button); every other child is the panel content. Use a Dialog instead for a focused flow, and a Tooltip for a one-line hint.",
  },
  Dialog: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
      open: z
        .boolean()
        .optional()
        .describe(
          'Bind two-way: { "$bindState": "/dialogs/reject" }, then a Button opens it with the setState action (statePath /dialogs/reject, value true). Without a binding it is only the initial state.',
        ),
      triggerLabel: z
        .string()
        .optional()
        .describe(
          "Renders an outline Button that opens the dialog, instead of binding `open`.",
        ),
      size: z
        .enum(["sm", "md", "lg"])
        .optional()
        .describe("Width: sm=384px, md=512px (default), lg=768px."),
    }),
    slots: ["default"],
    description:
      "A modal for a focused create, edit or confirm-with-input flow (a rejection reason, a short form). Children are the body; add one OverlayFooter child for the actions. Emits `close` when the user dismisses it with Escape, the X or the backdrop; footer buttons close it by setting `open` false with setState. Not for read-only information (use a Section, Popover or Sheet) and not for a plain yes/no (use ConfirmDialog).",
  },
  ConfirmDialog: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
      confirmLabel: z.string().optional().describe('Default "Confirm".'),
      cancelLabel: z.string().optional().describe('Default "Cancel".'),
      tone: z
        .enum(["default", "destructive"])
        .optional()
        .describe(
          "destructive styles the confirm button red; use it for deletes and anything irreversible.",
        ),
      open: z
        .boolean()
        .optional()
        .describe('Bind two-way: { "$bindState": "/dialogs/delete" }.'),
      triggerLabel: z
        .string()
        .optional()
        .describe(
          "Renders an outline Button that opens the confirm, instead of binding `open`.",
        ),
    }),
    slots: [],
    description:
      "A yes/no confirmation before a destructive or hard-to-undo action. State the consequence in the description. Emits `confirm` or `cancel` and closes itself. No body content: for input use a Dialog.",
  },
  Tooltip: {
    props: z.object({
      content: z.string(),
      side: placementSide.optional(),
    }),
    slots: ["default"],
    description:
      "A hover and focus hint wrapped around exactly ONE child (an icon button, a truncated value). Supplementary only: never the sole carrier of essential information, and touch users never see it. Keep `content` to a short phrase.",
  },
  HoverCard: {
    props: z.object({
      side: placementSide.optional(),
      align: placementAlign.optional(),
    }),
    slots: ["default"],
    description:
      "A preview of a record shown on hover or focus of its trigger. The FIRST child is the trigger (a Text, Badge or Button naming the record); every other child is the preview content (a few Fields). Supplementary: anything the user must act on belongs in the view, a Popover or a Sheet.",
  },
  Sheet: {
    props: z.object({
      title: z.string(),
      description: z.string().optional(),
      side: z.enum(["right", "left"]).optional().describe("Default right."),
      resizable: z
        .boolean()
        .optional()
        .describe(
          "Lets the user drag the sheet's inner edge to resize it. Use for wide content such as a table or a document.",
        ),
      open: z
        .boolean()
        .optional()
        .describe('Bind two-way: { "$bindState": "/panels/detail" }.'),
      triggerLabel: z
        .string()
        .optional()
        .describe(
          "Renders an outline Button that opens the sheet, instead of binding `open`.",
        ),
    }),
    slots: ["default"],
    description:
      "A side panel for a record's detail or a longer form that should keep the page visible behind it. Children are the body (it scrolls); add one OverlayFooter child for pinned actions. Emits `close` when dismissed with Escape, the X or the backdrop. Prefer it over a Dialog when the content is long or the user may want to refer back to the view.",
  },
  OverlayFooter: {
    props: z.object({}),
    slots: ["default"],
    description:
      "The action row of a Dialog or Sheet, pinned to its bottom and right-aligned. Put it as a child of the Dialog or Sheet, with Buttons inside: the secondary action first, the single primary last.",
  },
  ResizablePanels: {
    props: z.object({
      direction: z
        .enum(["horizontal", "vertical"])
        .optional()
        .describe("horizontal (default) places panels side by side."),
      height: z
        .number()
        .int()
        .min(160)
        .max(1200)
        .optional()
        .describe("Height in pixels (default 360)."),
    }),
    slots: ["default"],
    description:
      "A split view whose panels the user can drag to resize, e.g. a source document beside the form that captures it. Children MUST be ResizablePanel elements, two or three of them.",
  },
  ResizablePanel: {
    props: z.object({
      defaultSize: z
        .number()
        .min(1)
        .max(99)
        .optional()
        .describe(
          "Initial size as a percentage of the group. Default: an equal share.",
        ),
      minSize: z
        .number()
        .min(0)
        .max(99)
        .optional()
        .describe("Smallest size as a percentage (default 10)."),
    }),
    slots: ["default"],
    description:
      "One pane of ResizablePanels. Scrolls on overflow. Its children sit directly in the pane.",
  },
};

export type AppComponentName = keyof typeof APP_COMPONENTS;
export const APP_COMPONENT_NAMES = Object.keys(
  APP_COMPONENTS,
) as AppComponentName[];

/** Action `params` schemas keyed by action name. */
export type AppActionDefs = Record<
  string,
  { params: z.ZodType; description?: string }
>;

/**
 * Build the Apps catalog, optionally declaring the App's actions. An action is
 * how a control's event (a Button's `press`) reaches the App: the App's
 * framework supplies the handler, and `params` types what the control sends.
 */
export function buildAppCatalog(actions: AppActionDefs = {}) {
  return schema.createCatalog({
    components: APP_COMPONENTS,
    actions: Object.fromEntries(
      Object.entries(actions).map(([name, def]) => [
        name,
        { params: def.params, description: def.description ?? name },
      ]),
    ),
  });
}

export const appCatalog = buildAppCatalog();

/** A json-render spec targeting the Apps catalog. */
export type AppViewSpec = Spec;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The catalog schema requires `visible` and `children` on every element, but
 * the renderer treats them as optional (always visible, no children). Default
 * them so authored specs skip the boilerplate. Returns a copy.
 */
function withElementDefaults(spec: unknown): unknown {
  if (!isRecord(spec) || !isRecord(spec.elements)) return spec;
  const elements: Record<string, unknown> = {};
  for (const [id, element] of Object.entries(spec.elements)) {
    elements[id] = isRecord(element)
      ? { visible: true, children: [], ...element }
      : element;
  }
  return { ...spec, elements };
}

/**
 * Narrow an untyped view (e.g. one read off the API) to a spec the renderer
 * accepts: a root and an elements map. Catalog checks are `validateAppView`'s.
 */
export function isAppViewSpec(value: unknown): value is AppViewSpec {
  return isNonEmptySpec(value);
}

export interface AppViewValidationResult {
  valid: boolean;
  /** Human- and agent-readable issues, prefixed with the offending element key or path. */
  issues: string[];
}

/**
 * Validate an App view: structural integrity (root, dangling children,
 * misplaced `on`/`visible`) plus catalog membership and prop shapes.
 */
export function validateAppView(
  spec: unknown,
  actions: AppActionDefs = {},
): AppViewValidationResult {
  if (!isNonEmptySpec(spec)) {
    return {
      valid: false,
      issues: ["Spec is empty or missing a root/elements map."],
    };
  }
  const issues: string[] = [];
  for (const issue of validateSpec(spec).issues) {
    if (issue.severity !== "error") continue;
    issues.push(
      issue.elementKey
        ? `${issue.elementKey}: ${issue.message}`
        : issue.message,
    );
  }
  const catalog =
    Object.keys(actions).length > 0 ? buildAppCatalog(actions) : appCatalog;
  const result = catalog.validate(withElementDefaults(spec));
  if (!result.success && result.error) {
    for (const issue of result.error.issues) {
      issues.push(`${issue.path.join("/") || "(root)"}: ${issue.message}`);
    }
  }
  issues.push(...literalPropIssues(spec));
  return { valid: issues.length === 0, issues };
}

/** The json-render directive keys; a record made only of these is an expression. */
const EXPRESSION_KEYS: ReadonlySet<string> = new Set([
  "$state",
  "$bindState",
  "$bindItem",
  "$item",
  "$index",
  "$cond",
  "$then",
  "$else",
  "$template",
  "$computed",
]);

export function isExpression(value: unknown): boolean {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every((key) => EXPRESSION_KEYS.has(key));
}

/** True when `path` leads to an expression in `value`, or passes through one. */
function atExpression(value: unknown, path: readonly PropertyKey[]): boolean {
  let current = value;
  for (const key of path) {
    if (isExpression(current)) return true;
    if (Array.isArray(current) && typeof key === "number") {
      current = current[key];
    } else if (isRecord(current) && typeof key === "string") {
      current = current[key];
    } else {
      return false;
    }
  }
  return isExpression(current);
}

/**
 * Check every element's props against its catalog Zod schema. The catalog
 * schema above checks structure only, because any prop may be a binding
 * expression (`{ $state }`, `$cond`, ...) that resolves at render. So parse the
 * props as written and drop any issue located at or inside an expression, at
 * any depth: what is left are literals that are wrong, or required props that
 * are missing. Props the schema does not declare are stripped, as the schemas
 * themselves do, and are not reported. The renderer re-checks after resolution.
 */
function literalPropIssues(spec: unknown): string[] {
  if (!isRecord(spec) || !isRecord(spec.elements)) return [];
  return Object.entries(spec.elements).flatMap(([id, element]) => {
    if (!isRecord(element)) return [];
    const type = APP_COMPONENT_NAMES.find((name) => name === element.type);
    if (!type) return [];
    const props = isRecord(element.props) ? element.props : {};
    const parsed = APP_COMPONENTS[type].props.safeParse(props);
    if (parsed.success) return [];
    return parsed.error.issues
      .filter((issue) => !atExpression(props, issue.path))
      .map(
        (issue) =>
          `elements/${id}/${["props", ...issue.path].join("/")}: ${issue.message}`,
      );
  });
}

/** The catalog rendered as a prompt section, for agents that author App views. */
export function appCatalogPrompt(): string {
  return appCatalog.prompt();
}

/**
 * The catalog's reference sections (state, components, actions, events,
 * visibility, dynamic props), without json-render's JSONL output format and
 * generic rules. For an agent that hands a whole view to a tool rather than
 * streaming patches.
 */
export function appCatalogReference(): string {
  const prompt = appCatalogPrompt();
  const start = prompt.indexOf("INITIAL STATE:");
  const end = prompt.lastIndexOf("RULES:");
  return start >= 0 && end > start ? prompt.slice(start, end).trim() : prompt;
}
